import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import { SBA } from '../theme';
import useStudentProgress from '../hooks/useStudentProgress';
import { getAppNavItems } from '../utils/appNavItems';
import { subscribeStudyTracking, formatDuration, detectWeaknesses, weeklyStudyPercents } from '../utils/studyTracking';
import ProgressGraphs from '../components/dashboard/ProgressGraphs';
import { downloadExcelCsv } from '../utils/exportExcel';
import { printElementAsPdf } from '../utils/exportPdf';
import { buildQuizAiReport } from '../utils/studentQuizzes';

const PASS_THRESHOLD = 70;

function ProgressRing({ pct, color, size = 120, label }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(100, pct) / 100);

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 120 120" aria-hidden>
        <circle cx="60" cy="60" r={r} fill="none" stroke={SBA.surfaceContainerHigh} strokeWidth="10" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div style={s.ringCenter}>
        <span style={{ ...s.ringValue, color }}>{pct}%</span>
        {label && <span style={s.ringLabel}>{label}</span>}
      </div>
    </div>
  );
}

export default function MyProgressPage() {
  const { currentUser, isAdmin, subscriptionInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loading,
    totalLectures,
    completedCount,
    overallPct,
    subjects,
    examsTaken,
    examsPassed,
    avgExamScore,
    quizzes,
    quizSummary,
  } = useStudentProgress(currentUser);

  const [tracking, setTracking] = useState(null);
  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    return subscribeStudyTracking(currentUser.uid, setTracking);
  }, [currentUser?.uid]);

  const weaknesses = detectWeaknesses({ subjects, tracking, avgExamScore, quizzes });
  const quizAiReport = buildQuizAiReport(quizzes);

  function downloadReport() {
    const headers = ['Metric', 'Value'];
    const rows = [
      ['Overall completion', `${overallPct}%`],
      ['Lectures completed', `${completedCount}/${totalLectures}`],
      ['Exams taken', String(examsTaken)],
      ['Exams passed', `${examsPassed}/4`],
      ['Avg exam score', avgExamScore != null ? `${avgExamScore}%` : '—'],
      ['Lecture quizzes passed', `${quizSummary.lecturePassed}/${quizSummary.lectureTaken}`],
      ['Module quizzes passed', `${quizSummary.modulePassed}/${quizSummary.moduleTaken}`],
      ['RLA extended responses passed', `${quizSummary.erPassed || 0}/${quizSummary.erTaken || 0}`],
      ['Social Studies map readings passed', `${quizSummary.imrPassed || 0}/${quizSummary.imrTaken || 0}`],
      ['Avg quiz score', quizSummary.avgPct != null ? `${quizSummary.avgPct}%` : '—'],
      ['Portal login days', String(tracking?.loginDays || 0)],
      ['Total study time', formatDuration(tracking?.totalActiveSeconds || 0)],
      ['Idle time', formatDuration(tracking?.totalIdleSeconds || 0)],
      ...subjects.map((sub) => [`${sub.label} progress`, `${sub.pct}%`]),
      ...weaknesses.map((w) => [`Weakness: ${w.subject}`, w.reason]),
    ];
    downloadExcelCsv('my-progress-report.csv', headers, rows);
  }

  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Student';
  const navItems = getAppNavItems(isAdmin, location.pathname);
  const daysLeft = subscriptionInfo?.daysLeft;

  return (
    <AppShell navItems={navItems}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>My Progress</h1>
          <p style={s.subtitle}>
            Track your GED prep journey, {displayName.split(' ')[0]} — lectures, study time, and AI insights.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={downloadReport} style={s.reportBtn}>
            Download report (Excel)
          </button>
          <button
            type="button"
            onClick={() => printElementAsPdf('student-progress-report', 'My Progress Report')}
            style={s.reportBtn}
          >
            Download PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>Loading your progress…</div>
      ) : (
        <div id="student-progress-report">
          <ProgressGraphs
            records={{
              overallPct,
              weeklyPercents: weeklyStudyPercents(tracking?.dailyLogs),
              subjects,
              reports: [
                {
                  icon: 'quiz',
                  label: 'Lecture quizzes',
                  hint: `${quizSummary.lecturePassed} passed of ${quizSummary.lectureTaken} taken`,
                  status: quizSummary.lectureTaken ? `${quizSummary.lecturePassed} passed` : 'None yet',
                },
                {
                  icon: 'assignment',
                  label: 'Module quizzes',
                  hint: `${quizSummary.modulePassed} passed of ${quizSummary.moduleTaken} taken`,
                  status: quizSummary.moduleTaken ? `${quizSummary.modulePassed} passed` : 'None yet',
                },
                {
                  icon: 'edit_note',
                  label: 'RLA Extended Response',
                  hint: `${quizSummary.erPassed || 0} passed of ${quizSummary.erTaken || 0} taken`,
                  status: quizSummary.erTaken ? `${quizSummary.erPassed} passed` : 'None yet',
                },
                {
                  icon: 'map',
                  label: 'SS Images & map Readings',
                  hint: `${quizSummary.imrPassed || 0} passed of ${quizSummary.imrTaken || 0} taken`,
                  status: quizSummary.imrTaken ? `${quizSummary.imrPassed} passed` : 'None yet',
                },
                {
                  icon: 'workspace_premium',
                  label: 'Practice exams',
                  hint: `${examsPassed}/4 subjects passed`,
                  status: avgExamScore != null ? `${avgExamScore}% avg` : 'Not taken',
                },
              ],
              quizLecturePct: quizSummary.lectureTaken
                ? Math.round((quizSummary.lecturePassed / Math.max(1, quizSummary.lectureTaken)) * 100)
                : 0,
              quizModulePct: quizSummary.moduleTaken
                ? Math.round((quizSummary.modulePassed / Math.max(1, quizSummary.moduleTaken)) * 100)
                : 0,
              quizAvgPct: quizSummary.avgPct || 0,
            }}
          />
          <div className="sba-study-grid-2" style={s.summaryGrid}>
            <div className="sba-hero-card-mobile" style={s.heroCard}>
              <ProgressRing pct={overallPct} color={SBA.primary} label="Overall" />
              <div style={s.heroText}>
                <p style={s.heroTitle}>Course completion</p>
                <p style={s.heroMeta}>
                  {completedCount} of {totalLectures} lectures completed
                </p>
                <button type="button" style={s.primaryBtn} onClick={() => navigate('/course')}>
                  Continue studying
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
                </button>
              </div>
            </div>

            <div style={s.statsGrid}>
              {[
                {
                  icon: 'menu_book',
                  label: 'Lectures done',
                  value: String(completedCount),
                  color: SBA.primaryContainer,
                },
                {
                  icon: 'quiz',
                  label: 'Exams taken',
                  value: String(examsTaken),
                  color: '#0ea5e9',
                },
                {
                  icon: 'emoji_events',
                  label: 'Exams passed',
                  value: `${examsPassed}/4`,
                  color: '#10b981',
                },
                {
                  icon: 'grade',
                  label: 'Avg. exam score',
                  value: avgExamScore != null ? `${avgExamScore}%` : '—',
                  color: SBA.tertiary,
                },
                {
                  icon: 'schedule',
                  label: 'Study time',
                  value: formatDuration(tracking?.totalActiveSeconds || 0),
                  color: '#8b5cf6',
                },
                {
                  icon: 'login',
                  label: 'Portal days',
                  value: String(tracking?.loginDays || 0),
                  color: '#f59e0b',
                },
                ...(daysLeft != null && !subscriptionInfo?.isAdmin
                  ? [{
                    icon: 'schedule',
                    label: 'Subscription left',
                    value: `${daysLeft}d`,
                    color: SBA.secondaryContainer,
                  }]
                  : []),
              ].map(({ icon, label, value, color }) => (
                <div key={label} style={s.statCard}>
                  <div style={{ ...s.statIcon, background: color + '22', color }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 22 }}>{icon}</span>
                  </div>
                  <p style={s.statValue}>{value}</p>
                  <p style={s.statLabel}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 8, marginBottom: 24 }}>
            <h2 style={s.sectionTitle}>AI quiz monitor</h2>
            <p style={s.subtitle}>{quizAiReport.narrative}</p>
            {quizzes.length === 0 ? (
              <p style={s.heroMeta}>Complete a lecture to generate your first quiz. This report updates automatically.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {quizzes
                  .filter((q) => (q.attempts || 0) > 0)
                  .sort((a, b) => (b.lastPct || 0) - (a.lastPct || 0))
                  .map((q) => (
                    <div
                      key={q.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                        padding: '12px 16px',
                        borderRadius: 12,
                        background: q.passed ? '#f0fdf4' : '#fffbeb',
                        border: `1px solid ${q.passed ? '#bbf7d0' : '#fde68a'}`,
                        fontSize: 13,
                      }}
                    >
                      <div>
                        <strong>{q.title || q.id}</strong>
                        <span style={{ color: '#64748b' }}>
                          {' '}
                          · {q.kind === 'module' ? 'Module quiz' : 'Lecture quiz'}
                        </span>
                      </div>
                      <span>
                        {q.lastPct != null ? `${q.lastPct}%` : '—'} · {q.attempts} {q.attempts === 1 ? 'try' : 'tries'}
                        {q.passed ? ' · Passed' : q.cleared ? ' · Continued after tries' : ' · In progress'}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {weaknesses.length > 0 ? (
            <div style={{ marginTop: 24, marginBottom: 24 }}>
              <h2 style={s.sectionTitle}>AI weakness detection</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {weaknesses.map((w, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '12px 16px',
                      borderRadius: 12,
                      background: w.severity === 'high' ? '#fef2f2' : '#fffbeb',
                      border: `1px solid ${w.severity === 'high' ? '#fecaca' : '#fde68a'}`,
                      fontSize: 13,
                    }}
                  >
                    <strong>{w.subject}:</strong> {w.reason}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <h2 style={s.sectionTitle}>Subject breakdown</h2>
          <div className="sba-study-grid-2" style={s.subjectGrid}>
            {subjects.map((subject) => (
              <div key={subject.id} style={s.subjectCard}>
                <div style={s.subjectTop}>
                  <div style={{ ...s.subjectIcon, background: subject.color + '18', color: subject.color }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 26 }}>{subject.icon}</span>
                  </div>
                  <div style={s.subjectHeadText}>
                    <p style={s.subjectName}>{subject.label}</p>
                    <p style={s.subjectMeta}>
                      {subject.done}/{subject.total} lectures · {subject.pct}% complete
                      {subject.id === 'language-arts' && subject.erTotal > 0
                        ? ` · ER ${subject.erPassed}/${subject.erTotal}`
                        : ''}
                      {subject.id === 'social-studies' && subject.imrTotal > 0
                        ? ` · Maps ${subject.imrPassed}/${subject.imrTotal}`
                        : ''}
                    </p>
                  </div>
                  {subject.examPassed && (
                    <span style={s.passBadge}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>verified</span>
                      Passed
                    </span>
                  )}
                </div>

                <div style={s.progressTrack}>
                  <div style={{ ...s.progressFill, width: `${subject.pct}%`, background: subject.color }} />
                </div>

                <div style={s.examRow}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: subject.color }}>quiz</span>
                  <span style={s.examLabel}>Practice exam</span>
                  {subject.examPct != null ? (
                    <span style={s.examScore(subject.examPct >= PASS_THRESHOLD)}>
                      Best: {subject.examPct}%
                    </span>
                  ) : (
                    <span style={s.examPending}>
                      {subject.lecturesUnlocked || subject.total === 0 ? 'Not taken yet' : 'Complete lectures to unlock'}
                    </span>
                  )}
                </div>

                {subject.modulesDetail.length > 0 && (
                  <div style={s.moduleList}>
                    {subject.modulesDetail.map((mod) => (
                      <div key={mod.id} style={s.moduleRow}>
                        <span style={s.moduleTitle}>{mod.title}</span>
                        <span style={s.modulePct}>{mod.done}/{mod.total}</span>
                        <div style={s.moduleTrack}>
                          <div style={{ ...s.moduleFill, width: `${mod.pct}%`, background: subject.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={s.cardActions}>
                  <button
                    type="button"
                    style={{ ...s.outlineBtn, borderColor: subject.color, color: subject.color }}
                    onClick={() => navigate('/course')}
                  >
                    Open course
                  </button>
                  <button
                    type="button"
                    style={s.outlineBtn}
                    onClick={() => navigate('/exams')}
                    disabled={!subject.lecturesUnlocked && subject.total > 0}
                  >
                    View exams
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}

const s = {
  header: { marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  reportBtn: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#1b365d',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
  },
  title: {
    fontFamily: SBA.fontDisplay,
    fontSize: 26,
    fontWeight: 800,
    color: SBA.onSurface,
    letterSpacing: '-0.02em',
    marginBottom: 6,
  },
  subtitle: { fontSize: 14, color: SBA.onSurfaceVariant, maxWidth: 560, lineHeight: 1.6 },
  loading: { padding: 48, textAlign: 'center', color: SBA.outline, fontSize: 14 },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 1.1fr) minmax(280px, 1.4fr)',
    gap: 20,
    marginBottom: 32,
  },
  heroCard: {
    background: '#fff',
    borderRadius: 24,
    padding: 24,
    border: '1px solid rgba(229,225,228,0.5)',
    boxShadow: '0 4px 24px rgba(75,0,209,0.04)',
    display: 'flex',
    alignItems: 'center',
    gap: 20,
  },
  ringCenter: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ringValue: { fontFamily: SBA.fontDisplay, fontSize: 22, fontWeight: 800, lineHeight: 1 },
  ringLabel: { fontSize: 11, color: SBA.outline, fontWeight: 600, marginTop: 2 },
  heroText: { flex: 1, minWidth: 0 },
  heroTitle: { fontFamily: SBA.fontDisplay, fontSize: 16, fontWeight: 800, color: SBA.onSurface, marginBottom: 6 },
  heroMeta: { fontSize: 13, color: SBA.onSurfaceVariant, marginBottom: 16 },
  primaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: SBA.primary,
    color: '#fff',
    border: 'none',
    borderRadius: 14,
    padding: '12px 18px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 14,
  },
  statCard: {
    background: '#fff',
    borderRadius: 20,
    padding: 18,
    border: '1px solid rgba(229,225,228,0.5)',
    boxShadow: '0 4px 24px rgba(75,0,209,0.04)',
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  statValue: { fontFamily: SBA.fontDisplay, fontSize: 22, fontWeight: 800, color: SBA.onSurface, marginBottom: 2 },
  statLabel: { fontSize: 11, color: SBA.outline, fontWeight: 500 },
  sectionTitle: {
    fontFamily: SBA.fontDisplay,
    fontSize: 18,
    fontWeight: 800,
    color: SBA.onSurface,
    marginBottom: 16,
  },
  subjectGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 16,
    paddingBottom: 24,
  },
  subjectCard: {
    background: '#fff',
    borderRadius: 24,
    padding: 20,
    border: '1px solid rgba(229,225,228,0.5)',
    boxShadow: '0 4px 24px rgba(75,0,209,0.04)',
  },
  subjectTop: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  subjectIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  subjectHeadText: { flex: 1, minWidth: 0 },
  subjectName: { fontFamily: SBA.fontDisplay, fontSize: 14, fontWeight: 800, color: SBA.onSurface, marginBottom: 4 },
  subjectMeta: { fontSize: 12, color: SBA.onSurfaceVariant },
  passBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 999,
    background: '#d1fae5',
    color: '#059669',
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
  },
  progressTrack: { height: 8, background: SBA.surfaceContainerHigh, borderRadius: 9999, overflow: 'hidden', marginBottom: 14 },
  progressFill: { height: '100%', borderRadius: 9999 },
  examRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    background: SBA.surfaceContainer,
    borderRadius: 12,
    marginBottom: 14,
  },
  examLabel: { flex: 1, fontSize: 12, fontWeight: 600, color: SBA.onSurfaceVariant },
  examScore: (passed) => ({
    fontSize: 12,
    fontWeight: 800,
    color: passed ? '#059669' : '#b45309',
  }),
  examPending: { fontSize: 11, color: SBA.outline, fontWeight: 500 },
  moduleList: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 },
  moduleRow: { display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 10px', alignItems: 'center' },
  moduleTitle: { fontSize: 12, fontWeight: 600, color: SBA.onSurface, gridColumn: '1 / 2' },
  modulePct: { fontSize: 11, color: SBA.outline, fontWeight: 600 },
  moduleTrack: { gridColumn: '1 / -1', height: 5, background: SBA.surfaceContainerHigh, borderRadius: 9999, overflow: 'hidden' },
  moduleFill: { height: '100%', borderRadius: 9999 },
  cardActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  outlineBtn: {
    flex: '1 1 120px',
    background: 'none',
    border: '1.5px solid #e2e8f0',
    borderRadius: 12,
    padding: '9px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    color: SBA.onSurfaceVariant,
  },
};
